/**
 * Axios setup with silent refresh and single-flight refresh lock
 */

import axios from 'axios';

const axiosServices = axios.create({ baseURL: import.meta.env.VITE_APP_API_URL || '/' });

// Enable credentials for httpOnly cookies
axiosServices.defaults.withCredentials = true;

// Single-flight refresh lock - prevents concurrent refresh requests
let refreshPromise = null;

// Single-flight guard for impersonation expired handler
let handlingImpersonationExpired = false;

const AUTH_REFRESH_BYPASS_PATHS = [
  '/v1/account/login',
  '/v1/account/register',
  '/v1/account/forgot-password',
  '/v1/account/reset-password',
  '/v1/account/login/verify-mfa'
];

/**
 * Set or clear the Authorization header.
 * Called by JWTContext.setSession().
 */
export function setAuthToken(token) {
  if (token) {
    axiosServices.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    delete axiosServices.defaults.headers.common.Authorization;
  }
}

/**
 * Attempt to refresh the access token.
 * Uses single-flight pattern to prevent concurrent refresh requests.
 */
async function refreshAccessToken() {
  // Single-flight: if refresh in progress, await it
  if (refreshPromise) return refreshPromise;

  refreshPromise = axiosServices.post('/v1/account/refresh')
    .then(res => {
      const newToken = res.data.serviceToken;
      setAuthToken(newToken);
      return newToken;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

export function shouldSkipAuthRefresh(requestUrl = '') {
  return AUTH_REFRESH_BYPASS_PATHS.some((path) => requestUrl.includes(path));
}

// ==============================|| INTERCEPTORS ||============================== //

// Request interceptor - no longer reads from localStorage
axiosServices.interceptors.request.use(
  async (config) => {
    // Authorization header is set via setAuthToken() from JWTContext
    // No localStorage access needed
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handles 401 with silent refresh
axiosServices.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 402) {
      window.dispatchEvent(new CustomEvent('billing:required', { detail: error.response?.data || {} }));
      return Promise.reject(error);
    }

    // Guard: Don't refresh if this WAS the refresh request
    if (originalRequest.url?.includes('/account/refresh')) {
      return Promise.reject(error);
    }

    // Public auth flows and MFA verification should surface their own errors.
    if (shouldSkipAuthRefresh(originalRequest.url)) {
      return Promise.reject(error);
    }

    // Guard: Don't retry more than once per request
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Guard: Don't auto-refresh during impersonation
      // Instead, signal impersonation expired so UI can handle it
      if (window.__isImpersonating) {
        // Single-flight: prevent multiple events if several requests 401 at once
        if (!handlingImpersonationExpired) {
          handlingImpersonationExpired = true;
          window.dispatchEvent(new Event('auth:impersonation_expired'));
          // Reset after a short delay to allow future impersonation expirations
          setTimeout(() => { handlingImpersonationExpired = false; }, 1000);
        }
        return Promise.reject(error);
      }

      try {
        const newToken = await refreshAccessToken();
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axiosServices(originalRequest);
      } catch (refreshError) {
        // Only logout on auth errors (401/403), not transient 5xx
        if (refreshError.response?.status === 401 ||
          refreshError.response?.status === 403) {
          window.dispatchEvent(new Event('auth:logout'));
        }
        // On 5xx, just fail the request, don't logout
        return Promise.reject(error);
      }
    }

    // For non-401 errors or already retried, reject with the original error
    // This preserves the AxiosError shape for consistent handling
    return Promise.reject(error);
  }
);

export default axiosServices;

export async function fetcher(args) {
  const [url, config] = Array.isArray(args) ? args : [args];

  const res = await axiosServices.get(url, { ...config });

  return res.data;
}

/**
 * Extract a user-friendly error message from any error shape.
 * 
 * Handles AxiosError, plain objects, strings, and any other error type
 * for consistent UI error display.
 * 
 * @param {any} err - The error to extract a message from
 * @returns {string} A user-friendly error message
 */
function coerceMessage(value) {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    if (!value.length) return '';
    const first = value[0];
    if (typeof first === 'string') return first;
    if (first && typeof first === 'object') {
      if (typeof first.msg === 'string') return first.msg;
      if (typeof first.message === 'string') return first.message;
    }
    try {
      return JSON.stringify(value);
    } catch (e) {
      return 'An error occurred';
    }
  }
  if (typeof value === 'object') {
    if (typeof value.msg === 'string') return value.msg;
    if (typeof value.message === 'string') return value.message;
    if (typeof value.detail === 'string') return value.detail;
    if (Array.isArray(value.detail)) {
      const inner = coerceMessage(value.detail);
      if (inner) return inner;
    }
    try {
      return JSON.stringify(value);
    } catch (e) {
      return 'An error occurred';
    }
  }
  return String(value);
}

export function getErrorMessage(err) {
  const candidates = [
    err?.response?.data?.error,
    err?.response?.data?.detail,
    err?.error,
    err?.detail,
    err?.message
  ];
  for (const candidate of candidates) {
    const message = coerceMessage(candidate);
    if (message) return message;
  }
  return typeof err === 'string' ? err : 'An error occurred';
}
